import React from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../ui/accordion";

/**
 * Premium FAQ — Radix accordion, single-open, orange ring + rotating "+" icon.
 *
 * Props:
 *  - testId
 *  - eyebrow (default "Knowledge Base")
 *  - heading (ReactNode)
 *  - subtitle
 *  - items: { q, a }[]  (answers support multi-line via \n)
 *  - defaultOpenIndex?: number  (default 0)
 */
export default function PremiumFAQ({
  testId,
  eyebrow = "Knowledge Base",
  heading,
  subtitle,
  items = [],
  defaultOpenIndex = 0,
}) {
  return (
    <section
      data-testid={testId}
      className="relative bg-liftile-cream py-24 md:py-32 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full pointer-events-none opacity-[0.15]"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(40px)",
        }}
      />

      <div className="container-x relative">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65 }}
          className="max-w-3xl mx-auto text-center mb-14 md:mb-16"
        >
          <div className="heading-eyebrow mb-3">{eyebrow}</div>
          <h2 className="display-2 text-liftile-matte-black">{heading}</h2>
          {subtitle && (
            <p className="font-body text-base md:text-lg text-liftile-matte-black/70 mt-6 leading-relaxed max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65, delay: 0.1 }}
          className="max-w-3xl mx-auto"
        >
          <Accordion
            type="single"
            collapsible
            defaultValue={`${testId}-${defaultOpenIndex}`}
            className="space-y-3"
          >
            {items.map((item, i) => (
              <AccordionItem
                key={item.q}
                value={`${testId}-${i}`}
                data-testid={`${testId}-item-${i}`}
                className="group/item border-0 bg-white rounded-xl shadow-[0_2px_10px_-4px_rgba(0,0,0,0.08)] hover:shadow-[0_10px_28px_-8px_rgba(0,0,0,0.12)] transition-all duration-400 data-[state=open]:shadow-[0_18px_45px_-12px_rgba(255,107,26,0.28)] data-[state=open]:ring-1 data-[state=open]:ring-liftile-orange/60"
              >
                <AccordionTrigger
                  data-testid={`${testId}-trigger-${i}`}
                  className="group/trigger w-full hover:no-underline [&>svg]:hidden px-6 md:px-7 py-5 md:py-6 text-left"
                >
                  <div className="flex items-start gap-5 w-full">
                    <span className="shrink-0 mt-1 font-heading text-xs uppercase tracking-[0.2em] text-liftile-orange font-semibold w-8">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="flex-1 font-heading text-base md:text-lg text-liftile-matte-black font-semibold leading-snug group-data-[state=open]/item:text-liftile-orange transition-colors duration-300">
                      {item.q}
                    </span>
                    <span
                      aria-hidden
                      className="shrink-0 grid place-items-center w-9 h-9 rounded-full border border-liftile-matte-black/10 text-liftile-matte-black/70 group-data-[state=open]/item:bg-liftile-orange group-data-[state=open]/item:text-white group-data-[state=open]/item:border-liftile-orange group-data-[state=open]/item:rotate-45 transition-all duration-400 ease-out"
                    >
                      <Plus className="w-4 h-4" strokeWidth={2.5} />
                    </span>
                  </div>
                </AccordionTrigger>

                <AccordionContent className="px-6 md:px-7 pb-6 md:pb-7 pt-0">
                  <div style={{ paddingLeft: "3.25rem" }}>
                    <div className="h-px bg-liftile-orange/30 mb-4" />
                    <p className="font-body text-sm md:text-base text-liftile-matte-black/75 leading-relaxed whitespace-pre-line">
                      {item.a}
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
